import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { validateToken, applyAsAgent } from '../../api/invitationApi';
import { formatPhoneNumber } from '../../utils/phoneUtils';

const PP = "'Poppins', sans-serif";
const inputSx = { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' };

const Label = ({ children, required }) => (
  <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
    {children}{required && <span style={{ color: '#EF4444' }}> *</span>}
  </Typography>
);

export default function AgentSignup() {
  const { token } = useParams();
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [inviterName, setInviterName] = useState('');
  const [intendedRole, setIntendedRole] = useState('Sales Agent');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', icNumber: '', address: '',
    email: '', phoneNumber: '', username: '', password: '', confirmPassword: '',
  });

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await validateToken(token);
        if (res.data.isValid) {
          setTokenValid(true);
          setInviterName(res.data.inviterName);
          if (res.data.intendedRole) setIntendedRole(res.data.intendedRole);
        } else {
          setErrorMsg(res.data.message || 'Invalid invitation link.');
        }
      } catch {
        setErrorMsg('Unable to validate invitation link.');
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName || !form.icNumber || !form.address.trim() || !form.phoneNumber || !form.username || !form.password) {
      setErrorMsg('Please fill in all required fields.');
      return;
    }
    if (/\s/.test(form.username)) {
      setErrorMsg('Username cannot contain spaces. Use letters, numbers, dot, hyphen, or underscore only.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    if (form.password.length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      await applyAsAgent({ token, ...form, firstName: form.firstName.toUpperCase(), lastName: form.lastName.toUpperCase(), email: (form.email || '').toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setErrorMsg(err.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isReferral = intendedRole === 'Referral';
  const registrationLabel = isReferral ? 'Referral Registration' : 'Agent Registration';
  const joinAsLabel = isReferral ? 'referral' : intendedRole === 'In-house Agent' ? 'in-house agent' : intendedRole === 'Leader' ? 'leader' : 'sales agent';
  const agreementLabel = isReferral ? 'Referral Agreement' : 'Sales Agent Agreement';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 480, width: '100%', p: 3 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src="/nexwaveLogo.png" alt="Nexwave" style={{ height: 48, marginBottom: 8 }} />
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>Join nexwave</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>{registrationLabel}</Typography>
        </Box>

        {!tokenValid ? (
          <Alert severity="error" sx={{ borderRadius: '8px', fontFamily: PP }}>{errorMsg}</Alert>
        ) : submitted ? (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ borderRadius: '8px', fontFamily: PP, mb: 2 }}>
              Application submitted successfully!
            </Alert>
            <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontFamily: PP }}>
              You were invited by <strong>{inviterName}</strong>. Your application is now pending review.
              Once it's approved, you can log in with your chosen username and password — and you will see the
              {' '}{agreementLabel} on first login.
            </Typography>
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ borderRadius: '8px', fontFamily: PP, mb: 2 }}>
              You've been invited by <strong>{inviterName}</strong> to join nexwave as a {joinAsLabel}.
            </Alert>

            {errorMsg && <Alert severity="error" sx={{ borderRadius: '8px', fontFamily: PP, mb: 2 }}>{errorMsg}</Alert>}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Label required>First Name (Given Name)</Label>
                  <TextField size="small" fullWidth value={form.firstName} onChange={(e) => set('firstName', e.target.value)}
                    placeholder="e.g. Jin Sheng" InputProps={{ sx: inputSx }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Label required>Last Name (Surname / Family Name)</Label>
                  <TextField size="small" fullWidth value={form.lastName} onChange={(e) => set('lastName', e.target.value)}
                    placeholder="e.g. Tan" InputProps={{ sx: inputSx }} />
                </Box>
              </Box>

              <Box>
                <Label required>IC Number</Label>
                <TextField size="small" fullWidth value={form.icNumber} onChange={(e) => set('icNumber', e.target.value)} placeholder="e.g. 990101-14-1234" InputProps={{ sx: inputSx }} />
              </Box>

              <Box>
                <Label required>Address</Label>
                <TextField size="small" fullWidth multiline rows={2} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Full residential address" InputProps={{ sx: inputSx }} />
              </Box>

              <Box>
                <Label required>Phone Number</Label>
                <TextField size="small" fullWidth value={form.phoneNumber} onChange={(e) => set('phoneNumber', formatPhoneNumber(e.target.value))} placeholder="e.g. 012-345 6789" InputProps={{ sx: inputSx }} />
              </Box>

              <Box>
                <Label>Email</Label>
                <TextField size="small" fullWidth value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="email@example.com" InputProps={{ sx: inputSx }} />
              </Box>

              <Box sx={{ borderTop: '1px solid #E2E8F0', pt: 1.5, mt: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontFamily: PP }}>Account Credentials</Typography>
              </Box>

              <Box>
                <Label required>Username</Label>
                <TextField size="small" fullWidth value={form.username}
                  onChange={(e) => set('username', e.target.value.replace(/\s+/g, ''))}
                  placeholder="No spaces (e.g. jin.sheng or jin_sheng)"
                  helperText="Letters, numbers, dot, hyphen, underscore. No spaces. Must be unique."
                  FormHelperTextProps={{ sx: { fontSize: '0.68rem', fontFamily: PP, ml: 0.5, mt: 0.4 } }}
                  InputProps={{ sx: inputSx }} />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Label required>Password</Label>
                  <TextField size="small" fullWidth type="password" value={form.password} onChange={(e) => set('password', e.target.value)} InputProps={{ sx: inputSx }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Label required>Confirm</Label>
                  <TextField size="small" fullWidth type="password" value={form.confirmPassword} onChange={(e) => set('confirmPassword', e.target.value)} InputProps={{ sx: inputSx }} />
                </Box>
              </Box>

              <Button fullWidth variant="contained" onClick={handleSubmit} disabled={submitting} sx={{
                mt: 1, background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP,
                fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', py: 1.2,
                '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
              }}>
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </Box>
          </>
        )}

        <Typography sx={{ textAlign: 'center', mt: 2, fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP }}>
          nexwave
        </Typography>
      </Box>
    </Box>
  );
}
